<template>
  <div class="tableContainer" :id="scrollId">
    <table class="custom-table">
      <thead class="sticky-header">
        <tr>
          <th class="headerText" :style="{ borderBottom: tableLines ? 'none' : '1px solid #ddd' }" v-if="checkbox">
            <InputField
              :value="isSelectAll"
              :checkboxOptions="[{ id: 1}]"
              inputType="checkbox"
              @input="isSelectAll = !isSelectAll"
            >
            </InputField>
          </th>
          <th
            class="headerText"
            :style="{ borderBottom: tableLines ? 'none' : '1px solid #ddd', width: header.width !== null ? header.width : 'auto' }"
            v-for="(header, index) in headers"
            :key="header.value"
          >
            <span class="headerText">{{  header.required ? header.text + '*': header.text  }} </span>
            <v-icon
              v-if="sortable && sortOrders[index] === null && (header.sortable !== undefined ? header.sortable : true)"
              class="sortIcon"
              @click="sortClick(index, 'asc')"
            >
              arrow_drop_down
            </v-icon>
            <v-icon
              v-if="sortable && sortOrders[index] === 'desc' && (header.sortable !== undefined ? header.sortable : true)"
              class="sortIcon"
              @click="sortClick(index, null)"
            >
              arrow_drop_down
            </v-icon>
            <v-icon
              v-if="sortable && sortOrders[index] === 'asc' && (header.sortable !== undefined ? header.sortable : true)"
              class="sortIcon"
              @click="sortClick(index, 'desc')"
            >
              arrow_drop_up
            </v-icon>
          </th>
          <th v-if="actionButtons" class="headerText" :style="{ borderBottom: tableLines ? 'none' : '1px solid #ddd' }"></th>
          <th v-if="conflicts" class="headerText" :style="{ borderBottom: tableLines ? 'none' : '1px solid #ddd', textTransform: 'none' }">
            <input-field
              :key="'globalConflicts'"
              :input-type="'radio'"
              :value="globalConflicts"
              :size="'large'"
              inputContainerAlign="flex-end"
              inputFieldColor="var(--app-primary-base)"
              :inputFieldContainerStyling="{ 'padding': '0' }"
              :radioOptions="[{ value: 'New', label: 'New' }, { value: 'Old', label: 'Old' }]"
              @input="updateGlobalConflicts($event)"
            />
          </th>
          <th v-if="customButton" class="headerText" :style="{ borderBottom: tableLines ? 'none' : '1px solid #ddd' }"></th>
          <div v-if="addRowButton" class="addRowButton" >
            <CustomButton
              icon="add"
              :width="'20px'"
              :height="'20px'"
              :iconSize="12"
              :buttonStyling="{ padding: '8px', borderRadius: '4px' }"
              @buttonClick="handleAdd"
            />
          </div>
        </tr>
        <v-progress-linear
          v-if="loading"
          indeterminate
          class="loadingBar"
          color="var(--app-primary-base)"
          background-opacity="0.20"
        />
      </thead>
      <tbody>
        <div class="noDataContainer">
          <span v-if="items.length === 0 || items === null" class="noDataLabel">{{ $t("NoData") }}</span>
        </div>
        <tr
          id="rowId"
          class="bodyRow"
          v-for="(data, dataIndex) in items"
          :key="dataIndex"
          :class="{ 'selectedRow': selectedRowIndex === dataIndex }"
          @click="rowClick(dataIndex)"
        >
          <td v-if="checkbox" :style="{ borderBottom: tableLines ? 'none' : '1px solid #ddd' }" :class="tdWidth">
            <InputField
              inputType="checkbox"
              :value="isSelected(dataIndex)"
              :checkboxOptions="[{ id: dataIndex }]"
              @input="handleCheckboxChange(dataIndex)"
            >
            </InputField>
          </td>
          <td :key="dataIndex + '-' + rowIndexKey" :style="{ borderBottom: tableLines ? 'none' : '1px solid #ddd' }" class="rowText" v-for="(field, fieldIndex) in headers">
            <span v-if="!data.editable || !fieldIsEditable(field, dataIndex)" :class="{ password: data.isPassword && field.varName === 'value'}">{{ field.varName ? getVarNameValue(data, field) : data.value }}</span>
            <input-field
              v-else-if="actionButtons && data.editable && fieldIsEditable(field, dataIndex) && (field.type === 'select' || field.type === 'multiselect')"
              :input-type="field.type"
              :value="data[field.varName]"
              :placeholder="field.placeholder ? field.placeholder : null"
              :select-options-list="field.options"
              :inputStyle="{ 'border': 'none' }"
              :input-field-container-styling="{'padding': '0', 'border': 'none'}"
              @input="$emit('input', {data: data, index: dataIndex, field: field, value: $event})"
            >
              {{ data[field.varName] }}
            </input-field>
            <input
              v-else-if="actionButtons && data.editable && fieldIsEditable(field, dataIndex)"
              :placeholder="field.placeholder"
              :type="field.type ? field.type : 'text'"
              :min="field.min"
              :max="field.max"
              :step="field.step"
              class="inputClass"
              v-model="data[field.varName]"
              @keypress="checkInput($event, field.type, data[field.varName])"
            />
          </td>
          <td class="rowText rowButtons" :style="{ borderBottom: tableLines ? 'none' : '1px solid #ddd' }" v-if="actionButtons && !data.editable">
            <div class="rowButtonsContainer">
              <CustomButton
                icon="edit"
                :width="'20px'"
                :height="'20px'"

                :iconSize="12"
                :buttonStyling="{ padding: '8px', marginRight: '5px', borderRadius: '4px' }"
                @buttonClick="handleEdit(dataIndex)"
              />
              <CustomButton
                icon="mdi-delete"
                :width="'20px'"
                :height="'20px'"
                :disabled="deleteActionButtonDisable"
                :iconSize="12"
                :buttonStyling="{ padding: '8px', borderRadius: '4px' }"
                @buttonClick="handleDelete(dataIndex)"
              />
            </div>
          </td>
          <td class="rowText rowButtons" :style="{ borderBottom: tableLines ? 'none' : '1px solid #ddd' }" v-if="hasImportsButtons">
            <div class="rowButtonsContainer" :style="{ justifyContent: showValidate(data) ? 'center' : 'flex-end' }">
              <custom-button
                v-if="!isProcessing(data) && showValidate(data)"
                :label="$t('Validate')"
                :button-color="selectedRowIndex === dataIndex ? '#FFF' : 'var(--app-primary-base)'"
                :label-color="selectedRowIndex === dataIndex ? 'var(--app-primary-base)' : '#FFF'"
                :button-styling="validateButtonsStyling(dataIndex)"
                @buttonClick="handleValidate($event, data, dataIndex)"
              />
              <v-progress-circular
                v-if="isProcessing(data)"
                indeterminate
                size="20"
                width="3"
                color="accent"
                class="mr-3"
              />
              <v-icon
                :size="16"
                color="var(--app-primary-base)"
                :style="{ cursor: 'pointer', visibility: selectedRowIndex !== dataIndex ? 'hidden' : 'visible'}"
                :disabled="!hasDeletePermission"
                @click="handleImportDelete($event, dataIndex, selectedRowIndex !== dataIndex)"
              >
                {{ "mdi-delete" }}
              </v-icon>
            </div>
          </td>
          <td class="rowText rowButtons" :style="{ borderBottom: tableLines ? 'none' : '1px solid #ddd' }" v-if="actionButtons && data.editable">
            <div v-if="data.editable" class="rowButtonsContainerSaveDel">
              <CustomButton icon="done" :width="'20px'" :height="'20px'" :iconSize="12" :buttonStyling="{padding: '8px', marginRight: '5px', borderRadius: '4px'}" @buttonClick="handleSave(dataIndex)"/>
              <CustomButton icon="close" :width="'20px'" :height="'20px'" :iconSize="12" :buttonStyling="{padding: '8px', borderRadius: '4px'}" @buttonClick="handleCancel(dataIndex)"/>
            </div>
          </td>
          <td v-if="conflicts" :style="{ borderBottom: tableLines ? 'none' : '1px solid #ddd'}">
            <input-field
              :key="'conflicts-' + dataIndex"
              :input-type="'radio'"
              :value="getConflictValue(dataIndex)"
              :size="'large'"
              inputContainerAlign="flex-end"
              inputFieldColor="var(--app-primary-base)"
              :inputFieldContainerStyling="{ 'padding': '0'}"
              :radioOptions="[{ value: data.new_id, label: 'New' }, { value: data.id, label: 'Old' }]"
              @input.self="updateConflicts($event, dataIndex)"
              @click.stop
            />
          </td>
          <td v-if="customButton" :style="{ borderBottom: tableLines ? 'none' : '1px solid #ddd' }">
            <slot name="customButton" :data="data"></slot>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
import { bus } from "@/main.js";
import InputField from "@/newComponents/_Atoms/InputField.vue";
import CustomButton from "@/newComponents/_Atoms/CustomButton.vue";
import DialogChoice from "@/newComponents/_Molecules/DialogChoice.vue";
export default {
  props: {
    /**
     * Enable sorting.
     */
    sortable: {
      type: Boolean,
      default: false,
    },
    /**
     * Enable conflicts.
     */
     conflicts: {
      type: Boolean,
      default: false,
    },
    /**
     * Disable table lines.
     */
    tableLines: {
      type: Boolean,
      default: false,
    },
    /**
     * Loading bar
     */
    loading: {
      type: Boolean,
      default: false,
    },
    /**
     * Add custom button
     */
    customButton: {
      type: Boolean,
      default: false,
    },
    /**
     * Add action buttons.
     */
    actionButtons: {
      type: Boolean,
      default: false,
    },
    /**
     * Headers.
     */
    headers: {
      type: Array,
      default: () => [],
    },
    /**
     * Row items.
     */
    items: {
      type: Array,
      default: () => [],
    },
    /**
     * Enable checkboxes.
     */
    checkbox: {
      type: Boolean,
      default: false,
    },
    /**
     * Table button to add row.
     */
    addRowButton: {
      type: Boolean,
      default: false,
    },
    /**
     * Emmit a bus event on row click
     */
    emitRowClickBusEvent: {
      type: Boolean,
      default: false,
    },
    /**
     * Disable action button for delete
     */
    deleteActionButtonDisable: {
      type: Boolean,
      default: false,
    },

    scrollId: {
      type: String,
      default: 'scroll-bar',
    },

    tdWidth:{
      type: String,
      default: ''
    },
    hasImportsButtons:{
      type: Boolean,
      default: false,
    },
    hasDeletePermission:{
      type: Boolean,
      default: false,
    }
  },

  components: {
    InputField,
    CustomButton,
    DialogChoice
  },

  data() {
    return {
      sortOrders: Array(this.headers.length).fill(null),
      selectedItems: [],
      addButton: false,
      rowIndexKey: 0,
      editedRowIndex: null,
      editMode: false,
      selectedRowIndex: null,
      globalConflicts: null,
      selectedItemsConflicts: Array(this.items.length).fill(null),
      isSelectAll: false,
    };
  },

  mounted() {
    document.getElementById(this.scrollId).addEventListener("scroll", this.debouncedHandleScroll);

    bus.$on("add-table-row", this.handleAdd.bind(this)); // Bind the context
    bus.$on("clear-SelectedItems", this.clearSelected);
    bus.$on("clear-selected-row", this.clearSelectedRowIndex);
    bus.$on("clear-conflicts", this.clearConflicts);
  },

  destroyed() {
    bus.$off("add-table-row");
    bus.$off("clear-SelectedItems");
    bus.$off("clear-selected-row");
    bus.$off("clear-conflicts");
  },

  watch: {
    isSelectAll(newValue){
      if(newValue){
        //selectAll
        this.selectAll();
      }else{
        //deSelectAll
        this.deselectAll();
      }
    },

    'selectedItems.length'(newValue){
      //select all if selectItems length is same as items legth
      if(newValue === this.items.length && !this.isSelectAll){
        this.isSelectAll = true;
      }

      if(newValue === 0 && this.isSelectAll){
        this.isSelectAll = false;
      }
    },

    globalConflicts(newValue) {
      if(newValue === 'New'){
        this.selectedItemsConflicts = this.items.map(item => item.new_id);
      }else{
        this.selectedItemsConflicts = this.items.map(item => item.id);
      }
    },

    selectedItemsConflicts: {
      handler(newValue) {
        this.$emit("selected-conflicts", newValue);
      },
      deep: true
    }
  },

  methods: {
    debouncedHandleScroll:  _.debounce(function () {
      const scrollTop = document.getElementById(this.scrollId).scrollTop;
      const scrollHeight = document.getElementById(this.scrollId).scrollHeight;
      const containerHeight = document.getElementById(this.scrollId).offsetHeight;
      if (containerHeight + scrollTop + 1 >= scrollHeight) {
        this.$emit("onScrollBottom");
      }
      if (scrollTop === 0) {
        this.$emit("onScrollTop");
      }
    }, 200),

    isProcessing(data){
      return data.status === 'processing' || (data.status === 'Success' && data.step === 'upload');
    },

    showValidate(data){
      let showValidate = false;
      if(data.step === 'save_temporary_table' && data.total_conflicted_objects !== 0 && data.total_objects){
        showValidate = true;
      }else if((data.step === 'save_temporary_table' && data.total_conflicted_objects === 0) || (data.step === 'import_to_database' && data.status !== 'Success')){
        showValidate = true;
      }
      return showValidate;
    },

    handleImportDelete(event, index, restricDelete){
      event.stopPropagation();
      if(!this.hasDeletePermission || restricDelete){
        return;
      }
      this.handleDelete(index);
    },

    handleValidate(event, data, index){
      if (this.selectedRowIndex !== index) {
        this.selectedRowIndex = index;
      }
      event.stopPropagation();
      this.$emit('validate', data);
    },

    validateButtonsStyling(index){
      if(this.selectedRowIndex === index){
        return {
          'font-size': '10px',
          'line-height': '10px',
          'height': '20px',
          'font-weight': '600',
          'border-radius': '8px',
          'padding': '7px !important',
          'margin-right': '8px',
          'box-sizing': 'border-box',
          'border': '1px solid var(--app-primary-base)'
        }
      }
      return {
        'font-size': '10px',
        'line-height': '10px',
        'height': '20px',
        'font-weight': '600',
        'border-radius': '8px',
        'padding': '8px !important',
        'margin-right': '8px',
      }
    },

    stopPropagation(event) {
      event.stopPropagation();
    },

    updateGlobalConflicts(event) {
      this.globalConflicts = event;
    },

    clearConflicts(){
      this.globalConflicts = null;
      this.selectedItemsConflicts= Array(this.items.length).fill(null);
    },

    updateConflicts(event, dataIndex) {
      const copy = [...this.selectedItemsConflicts];
      copy[dataIndex] = event;
      this.selectedItemsConflicts = [...copy];
    },

    getConflictValue(dataIndex) {
      return this.selectedItemsConflicts[dataIndex] || null;
    },

    clearSelected() {
      this.selectedItems = []
    },

    clearSelectedAll() {
      if(this.isSelectAll){
        this.isSelectAll = false;
      }
    },

    handleAdd() {
      const newRow = {
        id: this.items.length + 1,
        editable: true,
      };
      this.addButton = true
      this.items.push(newRow);
      this.rowIndexKey++;
    },

    handleEdit(index) {
      this.items[index].editable = true;
      this.items[index].originalData = { ...this.items[index] };
      this.editMode = true;
      this.editedRowIndex = index;
      this.rowIndexKey++
    },

    handleDelete(index) {
      bus.$emit("editable-row-delete", this.items[index])
      this.items.splice(index, 1)
    },

    handleSave(index) {
      const requiredProperties = this.headers
        .filter(header => header.required)
        .map(header => header.value);

      const isAllPropertiesFilled = requiredProperties.every(prop => {
        return (
          this.items[index][prop] !== undefined &&
          this.items[index][prop] !== null &&
          String(this.items[index][prop]).trim() !== ""
        );
      });
      if (!isAllPropertiesFilled && !requiredProperties.length) {
        this.items.splice(index, 1);
      } else {
        this.items[index].editable = false;
        this.editMode = false;
        this.editedRowIndex = null;
        this.addButton = false;
        this.rowIndexKey++;
      }
      this.$emit("save", this.items[index]);
    },

    handleCancel(index) {
      this.items.splice(index, 1)
      this.editMode = false;
      this.editedRowIndex = null;
      // this.items.splice(index, 1);
      this.addButton = false;
    },

    sortClick(columnIndex, defaultSortOrder) {
      if (this.sortOrders[columnIndex] === null) {
        this.$set(this.sortOrders, columnIndex, "asc");
      } else if (this.sortOrders[columnIndex] === "asc") {
        this.$set(this.sortOrders, columnIndex, "desc");
      } else {
        this.$set(this.sortOrders, columnIndex, defaultSortOrder);
      }
      this.$emit(
        "sortClick",
        this.sortOrders[columnIndex],
        columnIndex,
      );
    },

    selectAll() {
      this.selectedItems = this.items.map((_, index) => index);
      this.$emit("selectedRows", [...this.items]);
    },

    deselectAll() {
      this.selectedItems = [];
      this.$emit("selectedRows", []);
    },

    handleCheckboxChange(dataIndex) {
      if (this.isSelected(dataIndex)) {
        this.selectedItems = this.selectedItems.filter(
          (index) => index !== dataIndex
        );
      } else {
        this.selectedItems.push(dataIndex);
      }
      this.$emit(
        "selectedRows",
        this.selectedItems.map((index) => this.items[index])
      );
    },
    isSelected(dataIndex) {
      return this.selectedItems.includes(dataIndex);
    },

    rowClick(index) {
      this.selectedRowIndex = index;
      
      if (this.emitRowClickBusEvent) {
        bus.$emit("on-row-click", this.items[this.selectedRowIndex]);
      }
    },


    clearSelectedRowIndex() {
      this.selectedRowIndex = null;
    },

    /**
     *
     * @param {Object} data the object containing the data
     * @param {String} field the object containing the field
     * If it's a single variable its value should be 'varName'
     * If there are multiple variables its value should be 'varName1,varName2,varName3,etc'
     * If it's a nested property its value should be 'object1.varName1'
     */
    getVarNameValue(data, field) {
      const varName = field.varName;
      let value = '';

      // in case it's needed a function to process the value
      if (field.function) {
        const keys = varName.split('.');
        let valueForFunction = keys.reduce((acc, key) => acc && acc[key], data);
        value = field.function(valueForFunction);
      }

      // if there's no need for processing the value
      else {
        // for multiple variables
        if (varName.includes(',')) {
          const varNamesKeys = varName.split(','); // separate the variables
          varNamesKeys.forEach((varNameKey, index) => {
            const keys = varNameKey.split('.');
            let keyValue = keys.reduce((acc, key) => acc && acc[key], data); // iterate through the data object to retrieve the value of the varNameKey
            if (keyValue) {
              value += index !== varNamesKeys.length-1 ? keyValue + ' ' : keyValue;
            }
          });
          return value;
        }
        // for a single variable
        else {
          const keys = varName.split('.');
          value = keys.reduce((acc, key) => acc && acc[key], data);
        }
      }
      return value;
    },

    fieldIsEditable(field, dataIndex) {
      return typeof field.editable === "boolean"
        ? field.editable
        : field.editable(dataIndex)
    },

    checkInput(e, inputType, value) {
      switch (inputType) {
        case "int_pos":
          this.checkIntegerPositive(e, value);
          break;
        case "int":
          this.checkIfMinusIsValid(e, value);
          this.checkInteger(e);
          break;
        case "number":
        case "float":
          this.checkIfMinusIsValid(e, value);
          this.checkFloat(e);
          break;
        case "number_pos":
        case "float_pos":
          this.checkFloatPositive(e);
          break;
        default:
          break;
      }
    },

    //only allow positive numbers
    checkFloat(e) {
      const regex = /^-?[\.,0-9]*$/;
      //if input is not a number a comma or a dot, validates to true
      if(!regex.test(e.key)){
        e.preventDefault();
      }
    },

    checkFloatPositive(e) {
      const regex = /^[\.,0-9]*$/;
      //if input is not a number a comma or a dot, validates to true
      if(!regex.test(e.key)){
        e.preventDefault();
      }
    },

    //only allow positive integers
    checkIntegerPositive(e, value) {
      const regex = /^[0-9]*$/;
      let currentValue = value ? String(value) : "";

      //if input is not a number validates to true
      if(!regex.test(e.key)){
        e.preventDefault();
        return
      }

      if(currentValue.length === 0 && e.key === "0"){
        e.preventDefault();
      }
    },

    checkInteger(e) {
      const regex = /^-?[0-9]*$/;
      //if input is not a number validates to true
      if(!regex.test(e.key)){
        e.preventDefault();
      }
    },

    checkIfMinusIsValid(e, value){
    let currentValue = value ? String(value) : "";
    let newkey= String(e.key);
    if(newkey.includes('-')){
        if(currentValue.length !=0){
          e.preventDefault();
        }
        if(currentValue.includes("-")){
        e.preventDefault();
      }
      }
    },
  },
};
</script>

<style>
.noDataContainer {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
  width: 100%;
}

.noDataLabel {
  color: var(--app-primary-base);
  text-align: center;
  font-family: Lato;
  font-size: 12px;
  font-style: normal;
  font-weight: 600;
  line-height: normal;
}

.tableContainer {
  max-height: 100%;
  height: 100%;
  overflow-y: auto;
  position: relative;
}

.addRowButton {
  position: absolute;
  top: 5px;
  right: 8px;
}

.bodyRow {
  position: relative;
}

.rowButtonsContainer {
  display: flex;
  align-items: center;
}

.rowButtonsContainerSaveDel {
  position: absolute;
  display: flex;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
}

.loadingBar {
  display: block;
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
}

.headerText {
  color: var(--app-primary-base);
  font-family: Lato;
  font-size: 10px;
  font-style: normal;
  font-weight: 600;
  line-height: 11.5px; /* 115% */
  text-transform: uppercase;
  height: 32.5px;
}

.inputClass {
  width: 100%;
}

.inputClass::placeholder {
  color: #DBDBE0;
  font-family: Lato;
  font-size: 8px;
  font-style: normal;
  font-weight: 400;
  line-height: 30px;
  text-transform: uppercase;
}

.rowText {
  color: #767676;
  font-family: Lato;
  font-size: 10px;
  font-style: normal;
  font-weight: 600;
  line-height: 11.5px; /* 115% */
  height: 32.5px;
}

.rowButtons {
  width: 60px;
}

.sticky-header {
  background-color: #fff;
  position: sticky;
  top: 0;
  z-index: 1;
}

.sticky-header::after {
  content: "";
  display: block;
  height: 1px;
  background-color: #ddd;
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
}

.custom-table {
  width: 100%;
  border-collapse: collapse;
}

.custom-table thead {
  background-color: #fff;
}

.custom-table th,
.custom-table td {
  padding: 8px;
  text-align: left;
  border-bottom: 1px solid #ddd;
}

.custom-table tbody tr:hover {
  background-color: #fafafa;
}

.headerText {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.sortIcon {
  height: 12px;
  width: 12px;
}

.v-icon.v-icon {
  font-size: 12px;
}

.bodyRow.selectedRow {
  background-color: #fafafa;
}

.v-progress-linear .v-progress-linear__indeterminate--active .short,
.v-progress-linear .v-progress-linear__indeterminate--active .long {
  background-color: var(--app-primary-base) !important;
}

.password {
  -webkit-text-security: disc;
}

.t-small {
  width: 25px;
  padding-right: 0px !important;
}
</style>



// WEBPACK FOOTER //
// src/newComponents/_Molecules/Table.vue